'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnSizingState, OnChangeFn, Updater } from '@tanstack/react-table';

export type TableColumnSizes<T extends object> = Record<keyof T, number>;
export type TableOnColumnResize<T extends object> = (state: TableColumnSizes<T>) => void;

const stableObjectRef = {};

function getInitialColumnSize<T extends object>(controlledSize?: TableColumnSizes<T>) {
  if (controlledSize) {
    return controlledSize;
  }

  return stableObjectRef;
}

export function useColumnSize<T extends object>(
  controlledColumnSize?: TableColumnSizes<T>,
  onColumnResize?: TableOnColumnResize<T>,
): [ColumnSizingState, OnChangeFn<ColumnSizingState>] {
  const hasUserUpdatedRef = useRef(false);
  const [state, setState] = useState<ColumnSizingState>(getInitialColumnSize(controlledColumnSize));
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const result = typeof updater === 'function' ? updater(state) : updater;
      setState(result);

      hasUserUpdatedRef.current = true;

      if (onColumnResize) {
        onColumnResize(result as TableColumnSizes<T>);
      }

      return result;
    },
    [state, onColumnResize],
  );

  useEffect(() => {
    if (!hasUserUpdatedRef.current) {
      setState(getInitialColumnSize(controlledColumnSize));
    }
  }, [controlledColumnSize]);

  return useMemo(() => [state, handleColumnSizingChange], [state, handleColumnSizingChange]);
}
